import { mapActions, mapState } from 'vuex';
import CodeInput from '@/components/generics/code-input/code-input.vue';
import switchButton from '@/components/generics/switch-button/switch-button.vue';
import validators from '@/helpers/validators';
import vuelidateMixin from '@/mixins/vuelidateMixin';

export default {
  mixins: [vuelidateMixin],
  components: {
    switchButton,
    CodeInput,
  },
  validations: {
    phoneNumber: validators.phone,
  },
  data() {
    return {
      loading: true,
      error: null,
      status: null,

      // Setup states
      setupMode: null, // 'email', 'phone', 'authenticator'
      setupStep: 'select', // 'select', 'send', 'verify', 'phone-input'

      // Email/Phone setup
      verificationCode: '',
      isSendingCode: false,
      isVerifying: false,
      codeSent: false,
      secondsUntilResend: 0,
      resendTimer: null,

      // Phone number input (for adding new phone)
      phoneNumber: '',
      pendingPhoneNumber: '',

      // Authenticator setup
      authenticatorData: null,
      codeCopied: false,

      // Confirmation modal for disabling 2FA
      showDisableConfirmation: false,
      isDisabling: false,
    };
  },
  computed: {
    ...mapState('user', ['userData']),

    is2FAEnabled() {
      return (this.status && this.status.is_two_factor_enabled) || false;
    },
    isEmailVerified() {
      return (this.status && this.status.is_email_verified) || false;
    },
    isPhoneVerified() {
      return (this.status && this.status.is_phone_verified) || false;
    },
    isAuthenticatorEnabled() {
      return (this.status && this.status.is_authenticator_enabled) || false;
    },
    emailHint() {
      return (this.status && this.status.email_hint) || '';
    },
    phoneHint() {
      return (this.status && this.status.phone_hint) || '';
    },
    hasPhone() {
      return !!this.phoneHint;
    },
    canEnable2FA() {
      return (this.status && this.status.can_enable_two_factor) || false;
    },
    hasAnyMethod() {
      return this.isEmailVerified || this.isPhoneVerified || this.isAuthenticatorEnabled;
    },
    canSendCode() {
      return !this.isSendingCode && this.secondsUntilResend === 0;
    },
    canVerify() {
      return this.verificationCode.length >= 6 && !this.isVerifying;
    },
    phoneError() {
      if (this.$v.phoneNumber.$error) {
        return 'Enter a valid phone number';
      }
      return '';
    },
    canSubmitPhone() {
      return !this.$v.phoneNumber.$invalid && !this.isSendingCode;
    },
    hasCodeError() {
      return this.error !== null && this.verificationCode === '';
    },
    currentStep() {
      // Returns step number for stepper indicator
      if (!this.setupMode) return 0;
      if (this.setupStep === 'phone-input') return 1;
      if (this.setupStep === 'send') return 1;
      if (this.setupStep === 'verify') return 2;
      return 1;
    },
    totalSteps() {
      // Email/Phone: Send -> Verify (2 steps)
      // Authenticator: Scan -> Verify (2 steps)
      // Phone with input: Input -> Send -> Verify (3 steps, but we show 2)
      return 2;
    },
  },
  mounted() {
    this.loadStatus();

    // Clear the navigation flag now that we've arrived
    this.setNavigatingToTwoFactorSetup(false);
  },
  beforeUnmount() {
    this.clearResendTimer();
  },
  methods: {
    ...mapActions('user', [
      'getTwoFactorStatus',
      'twoFactorSetupEmailSendCode',
      'twoFactorSetupEmailVerify',
      'twoFactorSetupPhoneSendCode',
      'twoFactorSetupPhoneVerify',
      'getTwoFactorSetupAuthenticator',
      'twoFactorSetupAuthenticatorVerify',
      'twoFactorEnable',
      'twoFactorDisable',
      'phoneUpdate',
      'phoneVerify',
      'setNavigatingToTwoFactorSetup',
    ]),

    async loadStatus() {
      this.loading = true;
      this.error = null;
      try {
        this.status = await this.getTwoFactorStatus();
      } catch (err) {
        this.error = 'Failed to load security settings.';
      } finally {
        this.loading = false;
      }
    },

    startSetup(method) {
      this.setupMode = method;
      this.verificationCode = '';
      this.codeSent = false;
      this.error = null;
      this.phoneNumber = '';
      this.pendingPhoneNumber = '';
      this.secondsUntilResend = 0;
      this.clearResendTimer();

      if (method === 'authenticator') {
        this.setupStep = 'send';
        this.loadAuthenticatorSetup();
      } else if (method === 'phone' && !this.hasPhone) {
        // User needs to enter a phone number first
        this.setupStep = 'phone-input';
      } else {
        // Auto-send code for email/phone methods (if phone exists)
        // Set both states synchronously to show spinner immediately (avoids button flash)
        this.isSendingCode = true;
        this.setupStep = 'send';
        this.doSendCode();
      }
    },

    // Internal method that skips the canSendCode guard (for initial auto-send)
    async doSendCode() {
      this.isSendingCode = true;
      this.error = null;

      try {
        let response;
        if (this.setupMode === 'email') {
          response = await this.twoFactorSetupEmailSendCode();
        } else if (this.setupMode === 'phone') {
          if (this.pendingPhoneNumber) {
            // Resending code to the pending phone number
            response = await this.phoneUpdate({ phoneNumber: this.pendingPhoneNumber });
          } else {
            response = await this.twoFactorSetupPhoneSendCode();
          }
        }

        this.codeSent = true;
        this.setupStep = 'verify';
        this.secondsUntilResend = (response && response.seconds_til_resend_available) || 60;
        this.startResendTimer();

        // Focus the code input after it appears
        this.$nextTick(() => {
          if (this.$refs.codeInput) {
            this.$refs.codeInput.focusFirst();
          }
        });
      } catch (err) {
        this.error = this.extractErrorMessage(err, 'Failed to send verification code.');
      } finally {
        this.isSendingCode = false;
      }
    },

    async cancelSetup() {
      this.setupMode = null;
      this.setupStep = 'select';
      this.verificationCode = '';
      this.codeSent = false;
      this.authenticatorData = null;
      this.secondsUntilResend = 0;
      this.clearResendTimer();
      this.codeCopied = false;
      await this.loadStatus();
    },

    async loadAuthenticatorSetup() {
      this.loading = true;
      this.codeCopied = false;
      try {
        this.authenticatorData = await this.getTwoFactorSetupAuthenticator();
        this.setupStep = 'verify';
      } catch (err) {
        this.error = this.extractErrorMessage(err, 'Failed to load authenticator setup.');
      } finally {
        this.loading = false;
      }
    },

    async copyManualCode() {
      if (!this.authenticatorData || !this.authenticatorData.manual_code) return;

      try {
        await navigator.clipboard.writeText(this.authenticatorData.manual_code);
        this.codeCopied = true;

        // Reset copied state after 2 seconds
        setTimeout(() => {
          this.codeCopied = false;
        }, 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.authenticatorData.manual_code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.codeCopied = true;

        setTimeout(() => {
          this.codeCopied = false;
        }, 2000);
      }
    },

    async sendCode() {
      if (!this.canSendCode) return;
      await this.doSendCode();
    },

    async submitPhoneNumber() {
      this.$v.phoneNumber.$touch();
      if (this.$v.phoneNumber.$invalid) {
        this.error = 'Please enter a valid phone number.';
        return;
      }

      this.isSendingCode = true;
      this.error = null;

      try {
        const response = await this.phoneUpdate({ phoneNumber: this.phoneNumber });
        this.pendingPhoneNumber = this.phoneNumber;
        this.codeSent = true;
        this.setupStep = 'verify';
        this.secondsUntilResend = (response && response.seconds_til_resend_available) || 60;
        this.startResendTimer();
      } catch (err) {
        this.error = this.extractErrorMessage(err, 'Failed to send verification code.');
      } finally {
        this.isSendingCode = false;
      }
    },

    onCodeComplete(code) {
      // Auto-submit when 6 digits are entered
      if (code && code.length === 6 && !this.isVerifying) {
        this.verifyCode();
      }
    },

    async verifyCode() {
      if (!this.canVerify) return;

      this.isVerifying = true;
      this.error = null;

      try {
        if (this.setupMode === 'email') {
          await this.twoFactorSetupEmailVerify({ code: this.verificationCode });
        } else if (this.setupMode === 'phone') {
          if (this.pendingPhoneNumber) {
            // Verifying a new phone number
            await this.phoneVerify({
              phoneNumber: this.pendingPhoneNumber,
              code: this.verificationCode,
            });
          } else {
            await this.twoFactorSetupPhoneVerify({ code: this.verificationCode });
          }
        } else if (this.setupMode === 'authenticator') {
          await this.twoFactorSetupAuthenticatorVerify({
            code: this.verificationCode,
            encryptedSecret: this.authenticatorData.encrypted_secret,
          });
        }

        // Reload status and reset
        await this.loadStatus();
        this.cancelSetup();
      } catch (err) {
        this.error = this.extractErrorMessage(err, 'Incorrect code. Please try again.');
        this.verificationCode = '';
      } finally {
        this.isVerifying = false;
      }
    },

    async toggleTwoFactor(enabled) {
      if (enabled && !this.is2FAEnabled) {
        // Enable 2FA
        try {
          await this.twoFactorEnable();
          await this.loadStatus();
        } catch (err) {
          this.error = this.extractErrorMessage(err, 'Failed to enable 2FA.');
        }
      } else if (!enabled && this.is2FAEnabled) {
        // Show confirmation modal before disabling
        this.showDisableConfirmation = true;
      }
    },

    cancelDisable() {
      this.showDisableConfirmation = false;
      // Force the toggle-button back to the correct "enabled" state
      // since is2FAEnabled didn't change, the watcher won't fire automatically
      if (this.$refs.twoFactorToggle) {
        this.$refs.twoFactorToggle.isChecked = this.is2FAEnabled;
      }
    },

    async confirmDisable() {
      this.isDisabling = true;
      this.error = null;
      try {
        await this.twoFactorDisable();
        await this.loadStatus();
        this.showDisableConfirmation = false;
      } catch (err) {
        this.error = this.extractErrorMessage(err, 'Failed to disable 2FA.');
      } finally {
        this.isDisabling = false;
      }
    },

    startResendTimer() {
      this.clearResendTimer();
      this.resendTimer = setInterval(() => {
        if (this.secondsUntilResend > 0) {
          this.secondsUntilResend -= 1;
        } else {
          this.clearResendTimer();
        }
      }, 1000);
    },

    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    },

    extractErrorMessage(error, defaultMessage) {
      // API returns errors as a direct array: response.data = [{ code, message, ... }]
      if (error.response && error.response.data) {
        const data = error.response.data;

        // Handle direct array of errors (API format)
        if (Array.isArray(data) && data.length > 0 && data[0].message) {
          return data[0].message;
        }

        // Handle nested errors array
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          return data.errors[0].message;
        }

        // Handle direct message property
        if (data.message) {
          return data.message;
        }
      }
      return defaultMessage;
    },

    getMethodStatusText(isVerified) {
      return isVerified ? 'Verified' : 'Not Set Up';
    },

    getMethodStatusClass(isVerified) {
      return isVerified
        ? 'security-settings__status--verified'
        : 'security-settings__status--not-verified';
    },
  },
};
